import type { XCapabilityProfile } from './types.js';

// Code points in these ranges weigh 1; everything else weighs 2.
const LIGHT_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0000, 0x10ff],
  [0x2000, 0x200d],
  [0x2010, 0x201f],
  [0x2032, 0x2037],
];

const isLight = (cp: number): boolean =>
  LIGHT_RANGES.some(([lo, hi]) => cp >= lo && cp <= hi);

// An emoji costs 2 for the whole sequence, however many code points it is built
// from. Matches a flag pair, a keycap, or a pictographic base with its optional
// variation selector, skin-tone modifier and ZWJ continuations.
// Written with escapes rather than literal characters: zero-width joiners and
// variation selectors are invisible in an editor and silently lost when copied.
const EMOJI_SEQUENCE =
  /(?:\p{RI}\p{RI}|[0-9#*]️?⃣|\p{Extended_Pictographic}(?:️|[\u{1F3FB}-\u{1F3FF}])?(?:‍\p{Extended_Pictographic}(?:️|[\u{1F3FB}-\u{1F3FF}])?)*)/gu;

const plainWeight = (text: string): number => {
  let total = 0;
  for (const ch of text) total += isLight(ch.codePointAt(0)!) ? 1 : 2;
  return total;
};

export const weightedLength = (text: string): number => {
  let total = 0;
  let cursor = 0;
  for (const m of text.matchAll(EMOJI_SEQUENCE)) {
    const at = m.index ?? 0;
    total += plainWeight(text.slice(cursor, at));
    total += 2;
    cursor = at + m[0].length;
  }
  return total + plainWeight(text.slice(cursor));
};

export const X_CAPABILITY: XCapabilityProfile = {
  version: '2026-08-13',
  platform: 'x',
  text: { max: 280, unit: 'weighted' },
  media: {
    maxCount: 4,
    maxBytes: 5_242_880,
    mimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
    altTextMax: 1000,
  },
};
